/**
 * DeckOps.ai Email Template Service
 * Professional email communications for executive authentication
 */

import { auth, db } from '../firebaseConfig.js';

class EmailTemplateService {
    constructor() {
        this.templates = this.initializeTemplates();
        console.log('📧 EmailTemplateService initialized with professional templates');
    }

    initializeTemplates() {
        return {
            welcome: this.getWelcomeTemplate(),
            companyVerification: this.getCompanyVerificationTemplate(),
            passwordReset: this.getPasswordResetTemplate(),
            accountLocked: this.getAccountLockedTemplate(),
            firmApproved: this.getFirmApprovedTemplate(),
            performanceMilestone: this.getPerformanceMilestoneTemplate(),
            weeklyPerformanceSummary: this.getWeeklyPerformanceSummaryTemplate(),
            competitiveAlert: this.getCompetitiveAlertTemplate(),
            adminNotification: this.getAdminNotificationTemplate()
        };
    }

    /**
     * Welcome & Email Verification Template
     * Used when new executives sign up for the platform
     */
    getWelcomeTemplate() {
        return {
            subject: "Welcome to DeckOps.ai - Verify Your Executive Account",
            html: `
            <!DOCTYPE html>
            <html lang="en">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Welcome to DeckOps.ai</title>
                <style>
                    /* Email-safe styles */
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 0; }
                    .email-template { width: 100%; max-width: 600px; margin: 0 auto; background-color: #ffffff; }
                    .email-header { background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); padding: 40px 40px 20px 40px; text-align: center; }
                    .email-logo { font-size: 32px; font-weight: 800; background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 50%, #ffffff 100%); background-clip: text; -webkit-background-clip: text; -webkit-text-fill-color: transparent; margin-bottom: 8px; display: inline-block; }
                    .email-tagline { color: rgba(255, 255, 255, 0.8); font-size: 14px; font-weight: 500; }
                    .email-content { padding: 40px; background: #ffffff; }
                    .email-greeting { color: #1e293b; font-size: 18px; font-weight: 600; margin-bottom: 20px; }
                    .email-title { color: #0f172a; font-size: 28px; font-weight: 800; line-height: 1.2; margin-bottom: 16px; }
                    .email-text { color: #475569; font-size: 16px; line-height: 1.6; margin-bottom: 24px; }
                    .email-text strong { color: #1e293b; font-weight: 600; }
                    .email-cta { text-align: center; margin: 32px 0; }
                    .cta-button { display: inline-block; background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%); color: white; text-decoration: none; padding: 18px 36px; border-radius: 12px; font-size: 16px; font-weight: 700; }
                    .email-secondary { background: #f8fafc; border-radius: 12px; padding: 24px; margin: 24px 0; border-left: 4px solid #00d4ff; }
                    .email-secondary h4 { color: #1e293b; font-size: 16px; font-weight: 700; margin-bottom: 12px; }
                    .email-secondary p { color: #64748b; font-size: 14px; line-height: 1.5; margin-bottom: 8px; }
                    .email-secondary ul { color: #64748b; font-size: 14px; line-height: 1.5; margin-left: 20px; }
                    .email-secondary li { margin-bottom: 6px; }
                    .backup-link { background: #f8fafc; border-radius: 8px; padding: 16px; margin: 20px 0; text-align: center; }
                    .backup-link p { color: #64748b; font-size: 13px; margin-bottom: 8px; }
                    .backup-link a { color: #0ea5e9; text-decoration: none; font-weight: 600; word-break: break-all; }
                    .security-notice { background: #fef3c7; border: 1px solid #fbbf24; border-radius: 8px; padding: 16px; margin: 24px 0; }
                    .security-notice h4 { color: #92400e; font-size: 14px; font-weight: 700; margin-bottom: 8px; }
                    .security-notice p { color: #b45309; font-size: 13px; line-height: 1.5; }
                    .email-footer { background: #f8fafc; padding: 32px 40px; text-align: center; border-top: 1px solid #e2e8f0; }
                    .footer-links { margin-bottom: 20px; }
                    .footer-links a { color: #0ea5e9; text-decoration: none; font-weight: 600; font-size: 14px; margin: 0 16px; }
                    .footer-company { color: #64748b; font-size: 14px; font-weight: 600; margin-bottom: 8px; }
                    .footer-address { color: #94a3b8; font-size: 12px; line-height: 1.5; margin-bottom: 16px; }
                    .footer-unsubscribe { color: #94a3b8; font-size: 11px; }
                    .footer-unsubscribe a { color: #64748b; text-decoration: none; }
                    @media only screen and (max-width: 600px) {
                        .email-header, .email-content, .email-footer { padding: 24px 20px !important; }
                        .email-title { font-size: 24px !important; }
                        .cta-button { padding: 16px 28px !important; font-size: 15px !important; }
                        .footer-links a { display: block; margin: 8px 0 !important; }
                    }
                </style>
            </head>
            <body>
                <div class="email-template">
                    <div class="email-header">
                        <div class="email-logo">DeckOps.ai</div>
                        <div class="email-tagline">Executive Performance Intelligence Platform</div>
                    </div>

                    <div class="email-content">
                        <div class="email-greeting">Welcome, {{displayName}}!</div>
                        
                        <h1 class="email-title">Verify Your Account</h1>
                        
                        <p class="email-text">
                            Thank you for joining <strong>DeckOps.ai</strong>, the premier executive performance intelligence platform trusted by Fortune 500 leaders worldwide.
                        </p>

                        <p class="email-text">
                            To complete your registration and access your personalized dashboard, please verify your email address by clicking the button below:
                        </p>

                        <div class="email-cta">
                            <a href="{{verificationLink}}" class="cta-button">Verify Your Account</a>
                        </div>

                        <div class="email-secondary">
                            <h4>What's Next?</h4>
                            <p>Once verified, you'll gain immediate access to:</p>
                            <ul>
                                <li><strong>Performance Analytics:</strong> Track your presentation and communication skills</li>
                                <li><strong>Competitive Intelligence:</strong> See how you rank against industry peers</li>
                                <li><strong>Professional Development:</strong> Access curated training modules</li>
                                <li><strong>Executive Insights:</strong> Receive data-driven performance recommendations</li>
                            </ul>
                        </div>

                        <div class="backup-link">
                            <p>Button not working? Copy and paste this link into your browser:</p>
                            <a href="{{verificationLink}}">{{verificationLink}}</a>
                        </div>

                        <div class="security-notice">
                            <h4>🔒 Security Notice</h4>
                            <p>This verification link will expire in 24 hours for your security. If you didn't create an account with DeckOps.ai, please ignore this email.</p>
                        </div>

                        <p class="email-text">
                            Welcome to the future of executive performance intelligence.
                        </p>

                        <p class="email-text">
                            <strong>The DeckOps.ai Team</strong>
                        </p>
                    </div>

                    <div class="email-footer">
                        <div class="footer-links">
                            <a href="https://deckops.ai/help">Help Center</a>
                            <a href="mailto:support@deckops.ai">Contact Support</a>
                            <a href="https://deckops.ai/privacy">Privacy Policy</a>
                        </div>
                        
                        <div class="footer-company">DeckOps.ai</div>
                        <div class="footer-address">
                            Executive Performance Intelligence Platform<br>
                            Trusted by Fortune 500 Leaders Worldwide
                        </div>
                        
                        <div class="footer-unsubscribe">
                            You received this email because you signed up for DeckOps.ai<br>
                            <a href="{{unsubscribeLink}}">Unsubscribe</a> | <a href="{{preferencesLink}}">Update Preferences</a>
                        </div>
                    </div>
                </div>
            </body>
            </html>
            `,
            variables: ['displayName', 'verificationLink', 'unsubscribeLink', 'preferencesLink']
        };
    }

    /**
     * Company Email Verification Template
     * Used when executives verify their work email for firm-specific features
     */
    getCompanyVerificationTemplate() {
        return {
            subject: "Verify Your {{firmName}} Email - Unlock Enterprise Features",
            html: `
            <!DOCTYPE html>
            <html lang="en">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Verify Your Company Email</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 0; }
                    .email-template { width: 100%; max-width: 600px; margin: 0 auto; background-color: #ffffff; }
                    .email-header { background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); padding: 40px 40px 20px 40px; text-align: center; }
                    .email-logo { font-size: 32px; font-weight: 800; background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 50%, #ffffff 100%); background-clip: text; -webkit-background-clip: text; -webkit-text-fill-color: transparent; margin-bottom: 8px; display: inline-block; }
                    .email-tagline { color: rgba(255, 255, 255, 0.8); font-size: 14px; font-weight: 500; }
                    .email-content { padding: 40px; background: #ffffff; }
                    .email-greeting { color: #1e293b; font-size: 18px; font-weight: 600; margin-bottom: 20px; }
                    .email-title { color: #0f172a; font-size: 28px; font-weight: 800; line-height: 1.2; margin-bottom: 16px; }
                    .email-text { color: #475569; font-size: 16px; line-height: 1.6; margin-bottom: 24px; }
                    .email-text strong { color: #1e293b; font-weight: 600; }
                    .email-cta { text-align: center; margin: 32px 0; }
                    .cta-button { display: inline-block; background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%); color: white; text-decoration: none; padding: 18px 36px; border-radius: 12px; font-size: 16px; font-weight: 700; }
                    .email-secondary { background: #f8fafc; border-radius: 12px; padding: 24px; margin: 24px 0; border-left: 4px solid #00d4ff; }
                    .email-secondary h4 { color: #1e293b; font-size: 16px; font-weight: 700; margin-bottom: 12px; }
                    .email-secondary p { color: #64748b; font-size: 14px; line-height: 1.5; margin-bottom: 8px; }
                    .email-secondary ul { color: #64748b; font-size: 14px; line-height: 1.5; margin-left: 20px; }
                    .email-secondary li { margin-bottom: 6px; }
                    .verification-code { background: #f1f5f9; border: 2px dashed #cbd5e1; border-radius: 8px; padding: 16px; text-align: center; margin: 20px 0; font-family: 'Courier New', monospace; font-size: 18px; font-weight: 700; color: #334155; letter-spacing: 2px; }
                    .backup-link { background: #f8fafc; border-radius: 8px; padding: 16px; margin: 20px 0; text-align: center; }
                    .backup-link p { color: #64748b; font-size: 13px; margin-bottom: 8px; }
                    .backup-link a { color: #0ea5e9; text-decoration: none; font-weight: 600; word-break: break-all; }
                    .security-notice { background: #fef3c7; border: 1px solid #fbbf24; border-radius: 8px; padding: 16px; margin: 24px 0; }
                    .security-notice h4 { color: #92400e; font-size: 14px; font-weight: 700; margin-bottom: 8px; }
                    .security-notice p { color: #b45309; font-size: 13px; line-height: 1.5; }
                    .email-footer { background: #f8fafc; padding: 32px 40px; text-align: center; border-top: 1px solid #e2e8f0; }
                    .footer-links { margin-bottom: 20px; }
                    .footer-links a { color: #0ea5e9; text-decoration: none; font-weight: 600; font-size: 14px; margin: 0 16px; }
                    .footer-company { color: #64748b; font-size: 14px; font-weight: 600; margin-bottom: 8px; }
                    .footer-address { color: #94a3b8; font-size: 12px; line-height: 1.5; margin-bottom: 16px; }
                    .footer-unsubscribe { color: #94a3b8; font-size: 11px; }
                    .footer-unsubscribe a { color: #64748b; text-decoration: none; }
                    @media only screen and (max-width: 600px) {
                        .email-header, .email-content, .email-footer { padding: 24px 20px !important; }
                        .email-title { font-size: 24px !important; }
                        .cta-button { padding: 16px 28px !important; font-size: 15px !important; }
                        .footer-links a { display: block; margin: 8px 0 !important; }
                    }
                </style>
            </head>
            <body>
                <div class="email-template">
                    <div class="email-header">
                        <div class="email-logo">DeckOps.ai</div>
                        <div class="email-tagline">Executive Performance Intelligence Platform</div>
                    </div>

                    <div class="email-content">
                        <div class="email-greeting">Welcome, {{displayName}}!</div>
                        
                        <h1 class="email-title">Verify Your Account</h1>
                        
                        <p class="email-text">
                            Thank you for joining <strong>DeckOps.ai</strong>, the premier executive performance intelligence platform trusted by Fortune 500 leaders worldwide.
                        </p>

                        <p class="email-text">
                            To complete your registration and access your personalized dashboard, please verify your email address by clicking the button below:
                        </p>

                        <div class="email-cta">
                            <a href="{{verificationLink}}" class="cta-button">Verify Your Account</a>
                        </div>

                        <div class="email-secondary">
                            <h4>What's Next?</h4>
                            <p>Once verified, you'll gain immediate access to:</p>
                            <ul>
                                <li><strong>Performance Analytics:</strong> Track your presentation and communication skills</li>
                                <li><strong>Competitive Intelligence:</strong> See how you rank against industry peers</li>
                                <li><strong>Professional Development:</strong> Access curated training modules</li>
                                <li><strong>Executive Insights:</strong> Receive data-driven performance recommendations</li>
                            </ul>
                        </div>

                        <div class="backup-link">
                            <p>Button not working? Copy and paste this link into your browser:</p>
                            <a href="{{verificationLink}}">{{verificationLink}}</a>
                        </div>

                        <div class="security-notice">
                            <h4>🔒 Security Notice</h4>
                            <p>This verification link will expire in 24 hours for your security. If you didn't create an account with DeckOps.ai, please ignore this email.</p>
                        </div>

                        <p class="email-text">
                            Welcome to the future of executive performance intelligence.
                        </p>

                        <p class="email-text">
                            <strong>The DeckOps.ai Team</strong>
                        </p>
                    </div>

                    <div class="email-footer">
                        <div class="footer-links">
                            <a href="https://deckops.ai/help">Help Center</a>
                            <a href="mailto:support@deckops.ai">Contact Support</a>
                            <a href="https://deckops.ai/privacy">Privacy Policy</a>
                        </div>
                        
                        <div class="footer-company">DeckOps.ai</div>
                        <div class="footer-address">
                            Executive Performance Intelligence Platform<br>
                            Trusted by Fortune 500 Leaders Worldwide
                        </div>
                        
                        <div class="footer-unsubscribe">
                            You received this email because you signed up for DeckOps.ai<br>
                            <a href="{{unsubscribeLink}}">Unsubscribe</a> | <a href="{{preferencesLink}}">Update Preferences</a>
                        </div>
                    </div>
                </div>
            </body>
            </html>
            `,
            variables: ['displayName', 'verificationLink', 'unsubscribeLink', 'preferencesLink']
        };
    }

    /**
     * Company Email Verification Template
     * Used for verifying work email addresses (McKinsey, Bain, BCG, etc.)
     */
    getCompanyVerificationTemplate() {
        return {
            subject: "Verify Your {{firmName}} Email - Unlock Enterprise Features",
            html: `
            <!DOCTYPE html>
            <html lang="en">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Verify Your Company Email</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 0; }
                    .email-template { width: 100%; max-width: 600px; margin: 0 auto; background-color: #ffffff; }
                    .email-header { background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); padding: 40px 40px 20px 40px; text-align: center; }
                    .email-logo { font-size: 32px; font-weight: 800; background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 50%, #ffffff 100%); background-clip: text; -webkit-background-clip: text; -webkit-text-fill-color: transparent; margin-bottom: 8px; display: inline-block; }
                    .email-tagline { color: rgba(255, 255, 255, 0.8); font-size: 14px; font-weight: 500; }
                    .email-content { padding: 40px; background: #ffffff; }
                    .email-greeting { color: #1e293b; font-size: 18px; font-weight: 600; margin-bottom: 20px; }
                    .email-title { color: #0f172a; font-size: 28px; font-weight: 800; line-height: 1.2; margin-bottom: 16px; }
                    .email-text { color: #475569; font-size: 16px; line-height: 1.6; margin-bottom: 24px; }
                    .email-text strong { color: #1e293b; font-weight: 600; }
                    .email-cta { text-align: center; margin: 32px 0; }
                    .cta-button { display: inline-block; background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%); color: white; text-decoration: none; padding: 18px 36px; border-radius: 12px; font-size: 16px; font-weight: 700; }
                    .email-secondary { background: #f8fafc; border-radius: 12px; padding: 24px; margin: 24px 0; border-left: 4px solid #00d4ff; }
                    .email-secondary h4 { color: #1e293b; font-size: 16px; font-weight: 700; margin-bottom: 12px; }
                    .email-secondary p { color: #64748b; font-size: 14px; line-height: 1.5; margin-bottom: 8px; }
                    .email-secondary ul { color: #64748b; font-size: 14px; line-height: 1.5; margin-left: 20px; }
                    .email-secondary li { margin-bottom: 6px; }
                    .verification-code { background: #f1f5f9; border: 2px dashed #cbd5e1; border-radius: 8px; padding: 16px; text-align: center; margin: 20px 0; font-family: 'Courier New', monospace; font-size: 18px; font-weight: 700; color: #334155; letter-spacing: 2px; }
                    .backup-link { background: #f8fafc; border-radius: 8px; padding: 16px; margin: 20px 0; text-align: center; }
                    .backup-link p { color: #64748b; font-size: 13px; margin-bottom: 8px; }
                    .backup-link a { color: #0ea5e9; text-decoration: none; font-weight: 600; word-break: break-all; }
                    .security-notice { background: #fef3c7; border: 1px solid #fbbf24; border-radius: 8px; padding: 16px; margin: 24px 0; }
                    .security-notice h4 { color: #92400e; font-size: 14px; font-weight: 700; margin-bottom: 8px; }
                    .security-notice p { color: #b45309; font-size: 13px; line-height: 1.5; }
                    .email-footer { background: #f8fafc; padding: 32px 40px; text-align: center; border-top: 1px solid #e2e8f0; }
                    .footer-links { margin-bottom: 20px; }
                    .footer-links a { color: #0ea5e9; text-decoration: none; font-weight: 600; font-size: 14px; margin: 0 16px; }
                    .footer-company { color: #64748b; font-size: 14px; font-weight: 600; margin-bottom: 8px; }
                    .footer-address { color: #94a3b8; font-size: 12px; line-height: 1.5; margin-bottom: 16px; }
                    .footer-unsubscribe { color: #94a3b8; font-size: 11px; }
                    .footer-unsubscribe a { color: #64748b; text-decoration: none; }
                </style>
            </head>
            <body>
                <div class="email-template">
                    <div class="email-header">
                        <div class="email-logo">DeckOps.ai</div>
                        <div class="email-tagline">Executive Performance Intelligence Platform</div>
                    </div>

                    <div class="email-content">
                        <div class="email-greeting">Hello {{displayName}},</div>
                        
                        <h1 class="email-title">Verify Your {{firmName}} Email</h1>
                        
                        <p class="email-text">
                            You're one step away from unlocking <strong>exclusive {{firmName}} features</strong> on DeckOps.ai. Please verify your company email address to access premium competitive intelligence tools.
                        </p>

                        <div class="email-cta">
                            <a href="{{verificationLink}}" class="cta-button">Verify Company Email</a>
                        </div>

                        <div class="email-secondary">
                            <h4>🏢 {{firmName}} Enterprise Benefits</h4>
                            <p>Email verification unlocks these exclusive features:</p>
                            <ul>
                                <li><strong>Firm-Specific Modules:</strong> {{firmName}} presentation standards and methodologies</li>
                                <li><strong>Internal Leaderboards:</strong> Compete with colleagues across global offices</li>
                                <li><strong>Advanced Analytics:</strong> Detailed performance insights and benchmarking</li>
                                <li><strong>Competitive Intelligence:</strong> Industry-leading metrics and best practices</li>
                                <li><strong>Priority Support:</strong> Dedicated assistance for enterprise users</li>
                            </ul>
                        </div>

                        <p class="email-text">
                            This verification confirms your {{firmName}} affiliation and grants access to our most sophisticated performance intelligence tools, designed specifically for management consulting excellence.
                        </p>

                        {{#verificationCode}}
                        <div class="verification-code">
                            Verification Code: {{verificationCode}}
                        </div>
                        {{/verificationCode}}

                        <div class="backup-link">
                            <p>Alternative verification link:</p>
                            <a href="{{verificationLink}}">{{verificationLink}}</a>
                        </div>

                        <div class="security-notice">
                            <h4>🔒 Enterprise Security</h4>
                            <p>This verification is required to maintain the integrity of our competitive intelligence platform. Link expires in 48 hours.</p>
                        </div>

                        <p class="email-text">
                            Thank you for choosing DeckOps.ai for your professional development.
                        </p>

                        <p class="email-text">
                            <strong>Enterprise Success Team</strong><br>
                            DeckOps.ai
                        </p>
                    </div>

                    <div class="email-footer">
                        <div class="footer-links">
                            <a href="https://deckops.ai/enterprise">Enterprise Portal</a>
                            <a href="https://deckops.ai/{{firmSlug}}-features">{{firmName}} Features</a>
                            <a href="mailto:enterprise@deckops.ai">Contact Support</a>
                        </div>
                        
                        <div class="footer-company">DeckOps.ai Enterprise</div>
                        <div class="footer-address">
                            Serving {{firmName}} Globally<br>
                            Premium Executive Intelligence Platform
                        </div>
                        
                        <div class="footer-unsubscribe">
                            This email was sent to your verified {{firmName}} address<br>
                            <a href="{{preferencesLink}}">Manage Preferences</a> | <a href="mailto:enterprise@deckops.ai">Enterprise Support</a>
                        </div>
                    </div>
                </div>
            </body>
            </html>
            `,
            variables: ['displayName', 'firmName', 'firmSlug', 'verificationLink', 'verificationCode', 'preferencesLink']
        };
    }

    /**
     * Password Reset Template
     * Used when executives request password reset
     */
    getPasswordResetTemplate() {
        return {
            subject: "Reset Your DeckOps.ai Password - Secure Access Request",
            html: `
            <!DOCTYPE html>
            <html lang="en">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Reset Your Password</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 0; }
                    .email-template { width: 100%; max-width: 600px; margin: 0 auto; background-color: #ffffff; }
                    .email-header { background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); padding: 40px 40px 20px 40px; text-align: center; }
                    .email-logo { font-size: 32px; font-weight: 800; background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 50%, #ffffff 100%); background-clip: text; -webkit-background-clip: text; -webkit-text-fill-color: transparent; margin-bottom: 8px; display: inline-block; }
                    .email-tagline { color: rgba(255, 255, 255, 0.8); font-size: 14px; font-weight: 500; }
                    .email-content { padding: 40px; background: #ffffff; }
                    .email-greeting { color: #1e293b; font-size: 18px; font-weight: 600; margin-bottom: 20px; }
                    .email-title { color: #0f172a; font-size: 28px; font-weight: 800; line-height: 1.2; margin-bottom: 16px; }
                    .email-text { color: #475569; font-size: 16px; line-height: 1.6; margin-bottom: 24px; }
                    .email-text strong { color: #1e293b; font-weight: 600; }
                    .email-cta { text-align: center; margin: 32px 0; }
                    .cta-button { display: inline-block; background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%); color: white; text-decoration: none; padding: 18px 36px; border-radius: 12px; font-size: 16px; font-weight: 700; }
                    .email-secondary { background: #f8fafc; border-radius: 12px; padding: 24px; margin: 24px 0; border-left: 4px solid #00d4ff; }
                    .email-secondary h4 { color: #1e293b; font-size: 16px; font-weight: 700; margin-bottom: 12px; }
                    .email-secondary p { color: #64748b; font-size: 14px; line-height: 1.5; margin-bottom: 8px; }
                    .email-secondary ul { color: #64748b; font-size: 14px; line-height: 1.5; margin-left: 20px; }
                    .email-secondary li { margin-bottom: 6px; }
                    .backup-link { background: #f8fafc; border-radius: 8px; padding: 16px; margin: 20px 0; text-align: center; }
                    .backup-link p { color: #64748b; font-size: 13px; margin-bottom: 8px; }
                    .backup-link a { color: #0ea5e9; text-decoration: none; font-weight: 600; word-break: break-all; }
                    .security-notice { background: #fef3c7; border: 1px solid #fbbf24; border-radius: 8px; padding: 16px; margin: 24px 0; }
                    .security-notice h4 { color: #92400e; font-size: 14px; font-weight: 700; margin-bottom: 8px; }
                    .security-notice p { color: #b45309; font-size: 13px; line-height: 1.5; }
                    .email-footer { background: #f8fafc; padding: 32px 40px; text-align: center; border-top: 1px solid #e2e8f0; }
                    .footer-links { margin-bottom: 20px; }
                    .footer-links a { color: #0ea5e9; text-decoration: none; font-weight: 600; font-size: 14px; margin: 0 16px; }
                    .footer-company { color: #64748b; font-size: 14px; font-weight: 600; margin-bottom: 8px; }
                    .footer-address { color: #94a3b8; font-size: 12px; line-height: 1.5; margin-bottom: 16px; }
                    .footer-unsubscribe { color: #94a3b8; font-size: 11px; }
                    .footer-unsubscribe a { color: #64748b; text-decoration: none; }
                </style>
            </head>
            <body>
                <div class="email-template">
                    <div class="email-header">
                        <div class="email-logo">DeckOps.ai</div>
                        <div class="email-tagline">Executive Performance Intelligence Platform</div>
                    </div>

                    <div class="email-content">
                        <div class="email-greeting">Hello {{displayName}},</div>
                        
                        <h1 class="email-title">Verify Your {{firmName}} Email</h1>
                        
                        <p class="email-text">
                            You're one step away from unlocking <strong>exclusive {{firmName}} features</strong> on DeckOps.ai. Please verify your company email address to access premium competitive intelligence tools.
                        </p>

                        <div class="email-cta">
                            <a href="{{verificationLink}}" class="cta-button">Verify Company Email</a>
                        </div>

                        <div class="email-secondary">
                            <h4>🏢 {{firmName}} Enterprise Benefits</h4>
                            <p>Email verification unlocks these exclusive features:</p>
                            <ul>
                                <li><strong>Firm-Specific Modules:</strong> {{firmName}} presentation standards and methodologies</li>
                                <li><strong>Internal Leaderboards:</strong> Compete with colleagues across global offices</li>
                                <li><strong>Advanced Analytics:</strong> Detailed performance insights and benchmarking</li>
                                <li><strong>Competitive Intelligence:</strong> Industry-leading metrics and best practices</li>
                                <li><strong>Priority Support:</strong> Dedicated assistance for enterprise users</li>
                            </ul>
                        </div>

                        {{#verificationCode}}
                        <div class="verification-code">
                            Verification Code: {{verificationCode}}
                        </div>
                        {{/verificationCode}}

                        <div class="backup-link">
                            <p>Alternative verification link:</p>
                            <a href="{{verificationLink}}">{{verificationLink}}</a>
                        </div>

                        <div class="security-notice">
                            <h4>🔒 Enterprise Security</h4>
                            <p>This verification is required to maintain the integrity of our competitive intelligence platform. Link expires in 48 hours.</p>
                        </div>

                        <p class="email-text">
                            <strong>Enterprise Success Team</strong><br>
                            DeckOps.ai
                        </p>
                    </div>

                    <div class="email-footer">
                        <div class="footer-links">
                            <a href="https://deckops.ai/enterprise">Enterprise Portal</a>
                            <a href="https://deckops.ai/{{firmSlug}}-features">{{firmName}} Features</a>
                            <a href="mailto:enterprise@deckops.ai">Contact Support</a>
                        </div>
                        
                        <div class="footer-company">DeckOps.ai Enterprise</div>
                        <div class="footer-address">
                            Serving {{firmName}} Globally<br>
                            Premium Executive Intelligence Platform
                        </div>
                        
                        <div class="footer-unsubscribe">
                            This email was sent to your verified {{firmName}} address<br>
                            <a href="{{preferencesLink}}">Manage Preferences</a> | <a href="mailto:enterprise@deckops.ai">Enterprise Support</a>
                        </div>
                    </div>
                </div>
            </body>
            </html>
            `,
            variables: ['displayName', 'firmName', 'firmSlug', 'verificationLink', 'verificationCode', 'preferencesLink']
        };
    }

    /**
     * Password Reset Template
     * Used for secure password reset functionality
     */
    getPasswordResetTemplate() {
        return {
            subject: "Reset Your DeckOps.ai Password - Secure Access Request",
            html: `
            <!DOCTYPE html>
            <html lang="en">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Reset Your Password</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 0; }
                    .email-template { width: 100%; max-width: 600px; margin: 0 auto; background-color: #ffffff; }
                    .email-header { background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); padding: 40px 40px 20px 40px; text-align: center; }
                    .email-logo { font-size: 32px; font-weight: 800; background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 50%, #ffffff 100%); background-clip: text; -webkit-background-clip: text; -webkit-text-fill-color: transparent; margin-bottom: 8px; display: inline-block; }
                    .email-tagline { color: rgba(255, 255, 255, 0.8); font-size: 14px; font-weight: 500; }
                    .email-content { padding: 40px; background: #ffffff; }
                    .email-greeting { color: #1e293b; font-size: 18px; font-weight: 600; margin-bottom: 20px; }
                    .email-title { color: #0f172a; font-size: 28px; font-weight: 800; line-height: 1.2; margin-bottom: 16px; }
                    .email-text { color: #475569; font-size: 16px; line-height: 1.6; margin-bottom: 24px; }
                    .email-text strong { color: #1e293b; font-weight: 600; }
                    .email-cta { text-align: center; margin: 32px 0; }
                    .cta-button { display: inline-block; background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%); color: white; text-decoration: none; padding: 18px 36px; border-radius: 12px; font-size: 16px; font-weight: 700; }
                    .email-secondary { background: #f8fafc; border-radius: 12px; padding: 24px; margin: 24px 0; border-left: 4px solid #00d4ff; }
                    .email-secondary h4 { color: #1e293b; font-size: 16px; font-weight: 700; margin-bottom: 12px; }
                    .email-secondary p { color: #64748b; font-size: 14px; line-height: 1.5; margin-bottom: 8px; }
                    .email-secondary ul { color: #64748b; font-size: 14px; line-height: 1.5; margin-left: 20px; }
                    .email-secondary li { margin-bottom: 6px; }
                    .backup-link { background: #f8fafc; border-radius: 8px; padding: 16px; margin: 20px 0; text-align: center; }
                    .backup-link p { color: #64748b; font-size: 13px; margin-bottom: 8px; }
                    .backup-link a { color: #0ea5e9; text-decoration: none; font-weight: 600; word-break: break-all; }
                    .security-notice { background: #fef3c7; border: 1px solid #fbbf24; border-radius: 8px; padding: 16px; margin: 24px 0; }
                    .security-notice h4 { color: #92400e; font-size: 14px; font-weight: 700; margin-bottom: 8px; }
                    .security-notice p { color: #b45309; font-size: 13px; line-height: 1.5; }
                    .email-footer { background: #f8fafc; padding: 32px 40px; text-align: center; border-top: 1px solid #e2e8f0; }
                    .footer-links { margin-bottom: 20px; }
                    .footer-links a { color: #0ea5e9; text-decoration: none; font-weight: 600; font-size: 14px; margin: 0 16px; }
                    .footer-company { color: #64748b; font-size: 14px; font-weight: 600; margin-bottom: 8px; }
                    .footer-address { color: #94a3b8; font-size: 12px; line-height: 1.5; margin-bottom: 16px; }
                    .footer-unsubscribe { color: #94a3b8; font-size: 11px; }
                    .footer-unsubscribe a { color: #64748b; text-decoration: none; }
                </style>
            </head>
            <body>
                <div class="email-template">
                    <div class="email-header">
                        <div class="email-logo">DeckOps.ai</div>
                        <div class="email-tagline">Executive Performance Intelligence Platform</div>
                    </div>

                    <div class="email-content">
                        <div class="email-greeting">Hello {{displayName}},</div>
                        
                        <h1 class="email-title">Reset Your Password</h1>
                        
                        <p class="email-text">
                            We received a request to reset the password for your DeckOps.ai account associated with <strong>{{userEmail}}</strong>.
                        </p>

                        <p class="email-text">
                            To create a new password, click the button below. This link will take you to a secure page where you can set up your new credentials.
                        </p>

                        <div class="email-cta">
                            <a href="{{resetLink}}" class="cta-button">Reset Password</a>
                        </div>

                        <div class="email-secondary">
                            <h4>🔐 Security Best Practices</h4>
                            <p>When creating your new password, please ensure it:</p>
                            <ul>
                                <li>Contains at least 12 characters</li>
                                <li>Includes uppercase and lowercase letters</li>
                                <li>Contains numbers and special characters</li>
                                <li>Is unique to your DeckOps.ai account</li>
                            </ul>
                        </div>

                        <div class="backup-link">
                            <p>If the button doesn't work, copy this link into your browser:</p>
                            <a href="{{resetLink}}">{{resetLink}}</a>
                        </div>

                        <div class="security-notice">
                            <h4>⚠️ Important Security Information</h4>
                            <p>This password reset link will expire in 1 hour for your security. If you didn't request this reset, please ignore this email and consider enabling two-factor authentication on your account.</p>
                        </div>

                        <p class="email-text">
                            If you continue to experience issues accessing your account, please contact our support team who can assist you further.
                        </p>

                        <p class="email-text">
                            <strong>Security Team</strong><br>
                            DeckOps.ai
                        </p>
                    </div>

                    <div class="email-footer">
                        <div class="footer-links">
                            <a href="https://deckops.ai/security">Security Center</a>
                            <a href="https://deckops.ai/help/account">Account Help</a>
                            <a href="mailto:security@deckops.ai">Contact Support</a>
                        </div>
                        
                        <div class="footer-company">DeckOps.ai</div>
                        <div class="footer-address">
                            Secure Executive Performance Platform<br>
                            Your privacy and security are our priority
                        </div>
                        
                        <div class="footer-unsubscribe">
                            This is a security-related email sent to your registered address<br>
                            <a href="https://deckops.ai/security-settings">Security Settings</a> | <a href="mailto:security@deckops.ai">Account Help</a>
                        </div>
                    </div>
                </div>
            </body>
            </html>
            `,
            variables: ['displayName', 'userEmail', 'resetLink']
        };
    }

    /**
     * Account Locked Template
     * Used when suspicious activity is detected
     */
    getAccountLockedTemplate() {
        return {
            subject: "DeckOps.ai Account Security Alert - Action Required",
            html: `
            <!DOCTYPE html>
            <html lang="en">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Account Security Alert</title>
                <style>
                    /* Same base styles as other templates */
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 0; }
                    .email-template { width: 100%; max-width: 600px; margin: 0 auto; background-color: #ffffff; }
                    .email-header { background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); padding: 40px 40px 20px 40px; text-align: center; }
                    .email-logo { font-size: 32px; font-weight: 800; background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 50%, #ffffff 100%); background-clip: text; -webkit-background-clip: text; -webkit-text-fill-color: transparent; margin-bottom: 8px; display: inline-block; }
                    /* Rest of styles same as other templates */
                    .alert-banner { background: #fef2f2; border: 2px solid #fca5a5; border-radius: 12px; padding: 20px; margin: 24px 0; text-align: center; }
                    .alert-banner h4 { color: #dc2626; font-size: 18px; font-weight: 700; margin-bottom: 8px; }
                    .alert-banner p { color: #991b1b; font-size: 16px; font-weight: 600; }
                </style>
            </head>
            <body>
                <div class="email-template">
                    <!-- Header same as other templates -->
                    <div class="email-content">
                        <div class="alert-banner">
                            <h4>🚨 Security Alert</h4>
                            <p>Your account has been temporarily locked</p>
                        </div>
                        
                        <div class="email-greeting">Hello {{displayName}},</div>
                        
                        <h1 class="email-title">Account Security Alert</h1>
                        
                        <p class="email-text">
                            We've detected <strong>suspicious activity</strong> on your DeckOps.ai account and have temporarily locked it to protect your data and competitive intelligence.
                        </p>

                        <div class="email-cta">
                            <a href="{{unlockLink}}" class="cta-button">Unlock My Account</a>
                        </div>

                        <div class="security-notice">
                            <h4>🔒 What Triggered This Alert?</h4>
                            <p>Multiple failed login attempts from unusual locations. If this wasn't you, your account credentials may be compromised.</p>
                        </div>
                        
                        <!-- Rest of template content -->
                    </div>
                </div>
            </body>
            </html>
            `,
            variables: ['displayName', 'unlockLink']
        };
    }

    /**
     * Firm Verification Approved Template
     * Used when company email is successfully verified
     */
    getFirmApprovedTemplate() {
        return {
            subject: "🎉 {{firmName}} Verification Complete - Enterprise Features Unlocked",
            html: `
            <!DOCTYPE html>
            <html>
            <head><meta charset="UTF-8"><title>Firm Verification Complete</title></head>
            <body style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 0;">
                <div style="width: 100%; max-width: 600px; margin: 0 auto; background: #ffffff;">
                    <div style="background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); padding: 40px; text-align: center;">
                        <div style="font-size: 32px; font-weight: 800; background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 50%, #ffffff 100%); background-clip: text; -webkit-background-clip: text; -webkit-text-fill-color: transparent; margin-bottom: 8px;">DeckOps.ai</div>
                        <div style="color: rgba(255, 255, 255, 0.8); font-size: 14px;">Executive Performance Intelligence Platform</div>
                    </div>
                    <div style="padding: 40px;">
                        <div style="color: #1e293b; font-size: 18px; font-weight: 600; margin-bottom: 20px;">Congratulations {{displayName}}!</div>
                        <h1 style="color: #0f172a; font-size: 28px; font-weight: 800; margin-bottom: 16px;">{{firmName}} Verification Complete</h1>
                        <p style="color: #475569; font-size: 16px; line-height: 1.6; margin-bottom: 24px;">Your {{firmName}} email has been successfully verified. You now have access to all enterprise features and competitive intelligence tools.</p>
                        <div style="text-align: center; margin: 32px 0;">
                            <a href="{{enterpriseLink}}" style="display: inline-block; background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%); color: white; text-decoration: none; padding: 18px 36px; border-radius: 12px; font-size: 16px; font-weight: 700;">Access Enterprise Dashboard</a>
                        </div>
                    </div>
                </div>
            </body>
            </html>
            `,
            variables: ['displayName', 'firmName', 'enterpriseLink']
        };
    }

    /**
     * Performance Milestone Template
     * Used when executives achieve significant performance milestones
     */
    getPerformanceMilestoneTemplate() {
        return {
            subject: "🏆 Performance Milestone Achieved - {{achievement}}",
            html: `
            <!DOCTYPE html>
            <html>
            <head><meta charset="UTF-8"><title>Performance Achievement</title></head>
            <body style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 0;">
                <div style="width: 100%; max-width: 600px; margin: 0 auto; background: #ffffff;">
                    <div style="background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); padding: 40px; text-align: center;">
                        <div style="font-size: 32px; font-weight: 800; background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 50%, #ffffff 100%); background-clip: text; -webkit-background-clip: text; -webkit-text-fill-color: transparent; margin-bottom: 8px;">DeckOps.ai</div>
                        <div style="color: rgba(255, 255, 255, 0.8); font-size: 14px;">Executive Performance Intelligence</div>
                    </div>
                    <div style="padding: 40px; text-align: center;">
                        <div style="font-size: 48px; margin-bottom: 20px;">🏆</div>
                        <h1 style="color: #0f172a; font-size: 28px; font-weight: 800; margin-bottom: 16px;">{{achievement}}</h1>
                        <p style="color: #475569; font-size: 18px; line-height: 1.6; margin-bottom: 24px;">Congratulations {{displayName}}! You've reached a significant performance milestone.</p>
                        <div style="background: #f8fafc; border-radius: 12px; padding: 24px; margin: 24px 0; border-left: 4px solid #00d4ff;">
                            <h4 style="color: #1e293b; font-weight: 700; margin-bottom: 12px;">Your Achievement Statistics</h4>
                            <p style="color: #64748b; font-size: 14px; margin-bottom: 8px;"><strong>Score:</strong> {{score}} points</p>
                            <p style="color: #64748b; font-size: 14px; margin-bottom: 8px;"><strong>Global Rank:</strong> #{{globalRank}}</p>
                            <p style="color: #64748b; font-size: 14px; margin-bottom: 8px;"><strong>{{firmName}} Rank:</strong> #{{firmRank}}</p>
                        </div>
                        <div style="text-align: center; margin: 32px 0;">
                            <a href="{{dashboardLink}}" style="display: inline-block; background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%); color: white; text-decoration: none; padding: 18px 36px; border-radius: 12px; font-size: 16px; font-weight: 700;">View Full Analytics</a>
                        </div>
                    </div>
                </div>
            </body>
            </html>
            `,
            variables: ['displayName', 'achievement', 'score', 'globalRank', 'firmRank', 'firmName', 'dashboardLink']
        };
    }

    /**
     * Weekly Performance Summary Template
     * Used for executive performance reports
     */
    getWeeklyPerformanceSummaryTemplate() {
        return {
            subject: "📊 Your Weekly Performance Intelligence Report",
            html: `
            <!DOCTYPE html>
            <html>
            <head><meta charset="UTF-8"><title>Weekly Performance Report</title></head>
            <body style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 0;">
                <div style="width: 100%; max-width: 600px; margin: 0 auto; background: #ffffff;">
                    <div style="background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); padding: 40px; text-align: center;">
                        <div style="font-size: 32px; font-weight: 800; background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 50%, #ffffff 100%); background-clip: text; -webkit-background-clip: text; -webkit-text-fill-color: transparent; margin-bottom: 8px;">DeckOps.ai</div>
                        <div style="color: rgba(255, 255, 255, 0.8); font-size: 14px;">Weekly Performance Intelligence Report</div>
                    </div>
                    <div style="padding: 40px;">
                        <div style="color: #1e293b; font-size: 18px; font-weight: 600; margin-bottom: 20px;">Hello {{displayName}},</div>
                        <h1 style="color: #0f172a; font-size: 28px; font-weight: 800; margin-bottom: 16px;">Your Week in Review</h1>
                        <p style="color: #475569; font-size: 16px; line-height: 1.6; margin-bottom: 24px;">Here's your executive performance intelligence summary for the week ending {{weekEnding}}.</p>
                        <!-- Performance metrics, comparisons, recommendations -->
                        <div style="text-align: center; margin: 32px 0;">
                            <a href="{{analyticsLink}}" style="display: inline-block; background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%); color: white; text-decoration: none; padding: 18px 36px; border-radius: 12px; font-size: 16px; font-weight: 700;">View Detailed Analytics</a>
                        </div>
                    </div>
                </div>
            </body>
            </html>
            `,
            variables: ['displayName', 'weekEnding', 'analyticsLink']
        };
    }

    /**
     * Competitive Intelligence Alert Template
     * Used for ranking changes and competitive updates
     */
    getCompetitiveAlertTemplate() {
        return {
            subject: "📈 Competitive Intelligence Alert - Ranking Update",
            html: `
            <!DOCTYPE html>
            <html>
            <head><meta charset="UTF-8"><title>Competitive Intelligence Alert</title></head>
            <body style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 0;">
                <div style="width: 100%; max-width: 600px; margin: 0 auto; background: #ffffff;">
                    <div style="background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); padding: 40px; text-align: center;">
                        <div style="font-size: 32px; font-weight: 800; background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 50%, #ffffff 100%); background-clip: text; -webkit-background-clip: text; -webkit-text-fill-color: transparent; margin-bottom: 8px;">DeckOps.ai</div>
                        <div style="color: rgba(255, 255, 255, 0.8); font-size: 14px;">Competitive Intelligence Platform</div>
                    </div>
                    <div style="padding: 40px;">
                        <div style="color: #1e293b; font-size: 18px; font-weight: 600; margin-bottom: 20px;">{{displayName}},</div>
                        <h1 style="color: #0f172a; font-size: 28px; font-weight: 800; margin-bottom: 16px;">Your Ranking Has Changed</h1>
                        <p style="color: #475569; font-size: 16px; line-height: 1.6; margin-bottom: 24px;">Your competitive position has been updated based on recent performance data.</p>
                        <div style="background: #f8fafc; border-radius: 12px; padding: 24px; margin: 24px 0; border-left: 4px solid #00d4ff;">
                            <h4 style="color: #1e293b; font-weight: 700; margin-bottom: 12px;">📊 Updated Rankings</h4>
                            <p style="color: #64748b; font-size: 14px; margin-bottom: 8px;"><strong>Global Rank:</strong> #{{newGlobalRank}} ({{globalRankChange}})</p>
                            <p style="color: #64748b; font-size: 14px; margin-bottom: 8px;"><strong>{{firmName}} Rank:</strong> #{{newFirmRank}} ({{firmRankChange}})</p>
                        </div>
                        <div style="text-align: center; margin: 32px 0;">
                            <a href="{{leaderboardLink}}" style="display: inline-block; background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%); color: white; text-decoration: none; padding: 18px 36px; border-radius: 12px; font-size: 16px; font-weight: 700;">View Leaderboard</a>
                        </div>
                    </div>
                </div>
            </body>
            </html>
            `,
            variables: ['displayName', 'newGlobalRank', 'globalRankChange', 'newFirmRank', 'firmRankChange', 'firmName', 'leaderboardLink']
        };
    }

    /**
     * Admin Notification Template
     * Used for system administrators
     */
    getAdminNotificationTemplate() {
        return {
            subject: "🔔 DeckOps.ai Admin Alert - {{alertType}}",
            html: `
            <!DOCTYPE html>
            <html>
            <head><meta charset="UTF-8"><title>Admin Alert</title></head>
            <body style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 0;">
                <div style="width: 100%; max-width: 600px; margin: 0 auto; background: #ffffff;">
                    <div style="background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); padding: 40px; text-align: center;">
                        <div style="font-size: 32px; font-weight: 800; color: #ffffff; margin-bottom: 8px;">DeckOps.ai</div>
                        <div style="color: rgba(255, 255, 255, 0.8); font-size: 14px;">Administrative Alert System</div>
                    </div>
                    <div style="padding: 40px;">
                        <div style="background: #fef3c7; border: 1px solid #fbbf24; border-radius: 12px; padding: 20px; margin-bottom: 24px; text-align: center;">
                            <h4 style="color: #92400e; font-size: 18px; font-weight: 700; margin-bottom: 8px;">🔔 {{alertType}}</h4>
                            <p style="color: #b45309; font-size: 16px; font-weight: 600;">{{alertMessage}}</p>
                        </div>
                        <div style="background: #f8fafc; border-radius: 12px; padding: 24px; margin: 24px 0;">
                            <h4 style="color: #1e293b; font-weight: 700; margin-bottom: 12px;">Alert Details</h4>
                            <p style="color: #64748b; font-size: 14px; margin-bottom: 8px;"><strong>Timestamp:</strong> {{timestamp}}</p>
                            <p style="color: #64748b; font-size: 14px; margin-bottom: 8px;"><strong>Severity:</strong> {{severity}}</p>
                            <p style="color: #64748b; font-size: 14px; margin-bottom: 8px;"><strong>Source:</strong> {{source}}</p>
                        </div>
                        <div style="text-align: center; margin: 32px 0;">
                            <a href="{{adminLink}}" style="display: inline-block; background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%); color: white; text-decoration: none; padding: 18px 36px; border-radius: 12px; font-size: 16px; font-weight: 700;">View Admin Console</a>
                        </div>
                    </div>
                </div>
            </body>
            </html>
            `,
            variables: ['alertType', 'alertMessage', 'timestamp', 'severity', 'source', 'adminLink']
        };
    }

    /**
     * Render template with variables
     */
    renderTemplate(templateName, variables = {}) {
        const template = this.templates[templateName];
        if (!template) {
            throw new Error(`Template '${templateName}' not found`);
        }

        let renderedHtml = template.html;
        let renderedSubject = template.subject;

        // Replace variables in both HTML and subject
        template.variables.forEach(varName => {
            const value = variables[varName] || '';
            const placeholder = `{{${varName}}}`;
            
            renderedHtml = renderedHtml.replace(new RegExp(placeholder, 'g'), value);
            renderedSubject = renderedSubject.replace(new RegExp(placeholder, 'g'), value);
        });

        return {
            subject: renderedSubject,
            html: renderedHtml
        };
    }

    /**
     * Send email using Firebase Email Extension
     */
    async sendEmail(templateName, recipientEmail, variables = {}) {
        try {
            const rendered = this.renderTemplate(templateName, variables);
            
            console.log(`📧 Sending ${templateName} email to ${recipientEmail}`);

            // Use Firebase Email Extension (mail collection)
            await db.collection('mail').add({
                to: [recipientEmail],
                message: {
                    subject: rendered.subject,
                    html: rendered.html
                },
                template: templateName,
                timestamp: new Date(),
                status: 'pending'
            });

            console.log(`✅ Email queued successfully: ${templateName}`);
            return { success: true, templateName, recipientEmail };

        } catch (error) {
            console.error(`❌ Error sending ${templateName} email:`, error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Send welcome verification email
     */
    async sendWelcomeEmail(user, verificationLink) {
        const variables = {
            displayName: user.displayName || user.email.split('@')[0],
            verificationLink: verificationLink,
            unsubscribeLink: `https://deckops.ai/unsubscribe?uid=${user.uid}`,
            preferencesLink: `https://deckops.ai/preferences?uid=${user.uid}`
        };

        return await this.sendEmail('welcome', user.email, variables);
    }

    /**
     * Send company verification email
     */
    async sendCompanyVerificationEmail(user, companyEmail, firmName, verificationLink, verificationCode = null) {
        const firmSlug = firmName.toLowerCase().replace(/[^a-z0-9]/g, '-');
        
        const variables = {
            displayName: user.displayName || user.email.split('@')[0],
            firmName: firmName,
            firmSlug: firmSlug,
            verificationLink: verificationLink,
            verificationCode: verificationCode,
            preferencesLink: `https://deckops.ai/preferences?uid=${user.uid}`
        };

        return await this.sendEmail('companyVerification', companyEmail, variables);
    }

    /**
     * Send password reset email
     */
    async sendPasswordResetEmail(userEmail, displayName, resetLink) {
        const variables = {
            displayName: displayName || userEmail.split('@')[0],
            userEmail: userEmail,
            resetLink: resetLink
        };

        return await this.sendEmail('passwordReset', userEmail, variables);
    }
}

// Create global instance
const emailTemplateService = new EmailTemplateService();

// Make available globally
window.emailTemplateService = emailTemplateService;

export default emailTemplateService;
